import Axios from "axios";
import { defineStore } from "pinia";
import pinia from "@/store/pinia";
import { getCurrentAuthToken, setCurrentAuthToken } from "./authToken";

function base64EncodeUnicode(str) {
  return btoa(
    encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, function (_, p1) {
      return String.fromCharCode("0x" + p1);
    })
  );
}

function createTokenClient() {
  return Axios.create({
    baseURL: "/",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    timeout: 30000,
  });
}

function createAuthorizedClient() {
  const token = getCurrentAuthToken()?.access_token;
  return Axios.create({
    baseURL: "/",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      ...(token ? { Authorization: "Bearer " + token } : {}),
    },
    timeout: 30000,
  });
}

export const useAuthStore = defineStore("auth", {
  state: () => ({
    token: getCurrentAuthToken(),
    userInfo: null,
  }),

  getters: {
    customer: (state) => state.userInfo?.Customer ?? null,
    provider: (state) => state.userInfo?.Provider ?? null,
    impersonationOptions: (state) => state.userInfo?.ImpersonationOptions ?? [],
    isLoggedIn: (state) => state.token != null,
    canShowPrices: (state) => Boolean(state.userInfo && state.userInfo.ShowPrices),
  },

  actions: {
    setToken(token) {
      this.token = token;
    },

    setUserInfo(userInfo) {
      this.userInfo = userInfo;
    },

    setAuthToken(token) {
      this.token = setCurrentAuthToken(token);
    },

    updateAuthToken(token) {
      this.setAuthToken(token);
    },

    setAuthUserInfo(userInfo) {
      this.setUserInfo(userInfo);
    },

    async loadCustomerUserInfo() {
      const response = await createAuthorizedClient().get("/api2/customer/userinfo");
      this.setUserInfo(response.data);

      const [{ useCartStore }, { useUiStore }] = await Promise.all([
        import("./cartStore"),
        import("./uiStore"),
      ]);

      useCartStore(pinia).loadShoppingCart();
      useUiStore(pinia).loadOpenJobsCount();
      console.log("User info updated");
    },

    async loadPartnerUserInfo() {
      const response = await createAuthorizedClient().get("/api2/partner/userinfo");
      this.setUserInfo(response.data);
      console.log("User info updated");
    },

    async loginCustomer({ username, password }) {
      const data = new FormData();
      data.append("grant_type", "client_credentials");
      data.append("scope", "ServiceProvider.Customer");
      const encodedUserPass = base64EncodeUnicode(username + ":" + password);

      try {
        const response = await createTokenClient().post("/api2/token", data, {
          headers: { Authorization: "Basic " + encodedUserPass },
        });
        this.setAuthToken(response.data);

        const { useRealtimeStore } = await import("./realtimeStore");
        useRealtimeStore(pinia).connect();
        this.loadCustomerUserInfo();
        return { success: true };
      } catch (error) {
        return { success: false, errorMessage: "Invalid password" };
      }
    },

    async loginPartner({ username, password }) {
      const data = new FormData();
      data.append("grant_type", "client_credentials");
      data.append("scope", "ServiceProvider.Partner");
      const encodedUserPass = base64EncodeUnicode(username + ":" + password);

      try {
        const response = await createTokenClient().post("/api2/token", data, {
          headers: { Authorization: "Basic " + encodedUserPass },
        });
        this.setAuthToken(response.data);
        this.loadCustomerUserInfo();
        return { success: true };
      } catch (error) {
        return { success: false, errorMessage: "Invalid password" };
      }
    },

    async loginPartnerCode(code) {
      console.log("Logging in partner");
      try {
        const response = await createTokenClient().get("/api2/partner/authentication", { params: { code } });
        console.log("Partner code login response", response);
        this.setAuthToken(response.data);
        this.loadPartnerUserInfo();
        return { success: true };
      } catch (error) {
        return { success: false, errorMessage: "Invalid password", error };
      }
    },

    async logoutUser() {
      this.setAuthToken(null);
      this.setUserInfo(null);

      const { useRealtimeStore } = await import("./realtimeStore");
      useRealtimeStore(pinia).disconnect();
    },

    async connectRealtime() {
      const { useRealtimeStore } = await import("./realtimeStore");
      return useRealtimeStore(pinia).connect();
    },

    async routeRealtime(payload) {
      const { useRealtimeStore } = await import("./realtimeStore");
      return useRealtimeStore(pinia).route(payload);
    },

    async subscribeRealtime(payload) {
      const { useRealtimeStore } = await import("./realtimeStore");
      return useRealtimeStore(pinia).subscribe(payload);
    },

    async unsubscribeRealtime(payload) {
      const { useRealtimeStore } = await import("./realtimeStore");
      return useRealtimeStore(pinia).unsubscribe(payload);
    },

    async sendRealtimeMessage(payload) {
      const { useRealtimeStore } = await import("./realtimeStore");
      return useRealtimeStore(pinia).message(payload);
    },
  },
});